// @system — small accessibility helpers shared by form and list components (docs/ACCESSIBILITY.md).
import { cloneElement, isValidElement } from 'react'

/**
 * Wire a single control to its label, hint and error message: the control gets `id` (unless it
 * has one), `aria-describedby` → hint/error paragraphs and `aria-invalid` while an error is
 * shown, so screen readers announce the message with the field (WCAG 1.3.1 / 3.3.1 / 4.1.2).
 * @param {import('react').ReactNode} children
 * @param {{ id: string, errorId?: string, error?: unknown, hintId?: string }} ids - `hintId` is appended while it is rendered
 * @returns {import('react').ReactNode}
 */
export function describeControl(children, { id, errorId, error, hintId }) {
  if (!isValidElement(children)) return children
  const props = /** @type {Record<string, unknown>} */ (children.props)
  const describedBy = [props['aria-describedby'], hintId, error ? errorId : null].filter(Boolean).join(' ') || undefined
  return cloneElement(children, {
    id: props.id ?? id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : props['aria-invalid'],
  })
}

/**
 * Keyboard activation for elements that act like buttons but are not <button>s (cards, list
 * items): Enter and Space trigger the handler like a native button (WCAG 2.1.1).
 * @param {(event: import('react').KeyboardEvent) => void} handler
 * @returns {(event: import('react').KeyboardEvent) => void}
 */
export function onActivateKey(handler) {
  return (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (event.target !== event.currentTarget) return // let nested controls handle their own keys
    event.preventDefault()
    handler(event)
  }
}

/** Interactive descendants a row/card click must not hijack (their own click already handled it). */
export const INTERACTIVE_SELECTOR = 'a[href], button, input, select, textarea, [role="button"], [role="switch"], [role="checkbox"], [role="menuitem"], [role="tab"], [contenteditable]'

/**
 * Row/card click handler that ignores clicks that originated on an interactive descendant.
 * @template T
 * @param {(row: T) => void} handler
 * @param {T} row
 * @returns {(event: import('react').MouseEvent) => void}
 */
export function rowClick(handler, row) {
  return (event) => {
    const target = /** @type {Element} */ (event.target)
    if (target.closest && target.closest(INTERACTIVE_SELECTOR) && target.closest(INTERACTIVE_SELECTOR) !== event.currentTarget) return
    handler(row)
  }
}
